import { useState, type FocusEvent } from 'react';
import { T, useGT, Var } from 'gt-react';
import {
  SystemEntity,
  type TaskTypeViewFieldPlacement,
} from '@erp/shared-types';
import { Checkbox } from 'src/components/ui/checkbox';
import { Input } from 'src/components/ui/input';
import { Label } from 'src/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from 'src/components/ui/select';
import { Textarea } from 'src/components/ui/textarea';
import {
  fieldControlKind,
  formatFieldDraft,
  numberInputStep,
  parseFieldDraft,
  type FieldControlKind,
} from './fieldControl';
import type { CorrectionCommitResult } from './useCommitTaskCorrection';
import { TaskTypeViewEntityReference } from './TaskTypeViewEntityReference';
import {
  findSchemaField,
  EMPTY_MARK,
  getValueAtPath,
  placementLabel,
  scalarValuesDiffer,
  setValueAtPath,
  valueText,
  type TaskViewSchemaField,
} from './taskTypeViewValue';

type Commit = (
  next: Record<string, unknown>,
) => void | Promise<CorrectionCommitResult | void>;

export function TaskTypeViewField({
  placement,
  schemaFields,
  input,
  workingCopy,
  onCommit,
  hideLabel = false,
  ariaLabel,
}: {
  placement: TaskTypeViewFieldPlacement;
  schemaFields: readonly TaskViewSchemaField[];
  input: Record<string, unknown>;
  workingCopy: Record<string, unknown>;
  onCommit: Commit;
  hideLabel?: boolean;
  ariaLabel?: string;
}) {
  const field = findSchemaField(schemaFields, placement.config.path);
  const label = placementLabel(
    placement.config.label,
    field,
    placement.config.path,
  );
  const inputValue = getValueAtPath(input, placement.config.path);
  const workingValue = getValueAtPath(workingCopy, placement.config.path);
  const name = ariaLabel ?? label;

  async function commitValue(value: unknown) {
    return onCommit(setValueAtPath(workingCopy, placement.config.path, value));
  }

  if (
    field?.fieldType === 'entity_reference' ||
    field?.fieldType === 'document'
  ) {
    return (
      <TaskTypeViewEntityReference
        placementId={placement.id}
        label={label}
        hideLabel={hideLabel}
        ariaLabel={name}
        mode={placement.config.mode}
        referencedEntityType={
          field.fieldType === 'document'
            ? SystemEntity.CORE_DOCUMENT
            : (field.referencedEntityType ?? null)
        }
        inputValue={inputValue}
        workingValue={workingValue}
        onCommit={commitValue}
      />
    );
  }

  return (
    <div
      className="flex min-w-0 flex-col gap-1"
      data-testid={`task-view-field-${placement.id}`}
    >
      {hideLabel ? null : <Label>{label}</Label>}
      {placement.config.mode === 'read' ? (
        <ReadValue value={inputValue} />
      ) : (
        <EditValue
          fieldType={field?.fieldType}
          enumValues={field?.enumValues ?? []}
          workingValue={workingValue}
          ariaLabel={name}
          onCommitValue={commitValue}
        />
      )}
      {placement.config.mode === 'edit' ? (
        <OriginalValue inputValue={inputValue} workingValue={workingValue} />
      ) : null}
    </div>
  );
}

function ReadValue({ value }: { value: unknown }) {
  const text = useDisplayText(value);
  if (text === null) return <EmptyMark />;
  return (
    <div
      className="whitespace-pre-wrap text-sm text-stone-800"
      data-testid="task-view-value"
    >
      {text}
    </div>
  );
}

function OriginalValue({
  inputValue,
  workingValue,
}: {
  inputValue: unknown;
  workingValue: unknown;
}) {
  const text = useDisplayText(inputValue);
  if (!scalarValuesDiffer(inputValue, workingValue)) return null;
  return (
    <p
      className="text-xs text-muted-foreground"
      data-testid="task-view-original"
    >
      <T context="secondary text under a task view field showing the extracted original value">
        Original: <Var>{text ?? EMPTY_MARK}</Var>
      </T>
    </p>
  );
}

function EmptyMark() {
  const gt = useGT();
  return (
    <span
      className="text-sm text-muted-foreground"
      aria-label={gt('Empty', {
        context: 'shown when a task view field has no value',
      })}
    >
      {EMPTY_MARK}
    </span>
  );
}

function useDisplayText(value: unknown): string | null {
  const gt = useGT();
  if (typeof value === 'boolean') {
    return value
      ? gt('Yes', { context: 'boolean true shown on a task view field' })
      : gt('No', { context: 'boolean false shown on a task view field' });
  }
  return valueText(value);
}

function EditValue({
  fieldType,
  enumValues,
  workingValue,
  ariaLabel,
  onCommitValue,
}: {
  fieldType: string | undefined;
  enumValues: readonly string[] | null | undefined;
  workingValue: unknown;
  ariaLabel: string;
  onCommitValue: (value: unknown) => Promise<CorrectionCommitResult | void>;
}) {
  const kind = fieldControlKind(fieldType);
  if (kind === 'boolean') {
    return (
      <BooleanControl
        workingValue={workingValue}
        ariaLabel={ariaLabel}
        onCommitValue={onCommitValue}
      />
    );
  }
  if (kind === 'enum') {
    return (
      <EnumControl
        workingValue={workingValue}
        enumValues={enumValues ?? []}
        ariaLabel={ariaLabel}
        onCommitValue={onCommitValue}
      />
    );
  }
  return (
    <DraftControl
      kind={kind}
      fieldType={fieldType}
      workingValue={workingValue}
      ariaLabel={ariaLabel}
      onCommitValue={onCommitValue}
    />
  );
}

function DraftControl({
  kind,
  fieldType,
  workingValue,
  ariaLabel,
  onCommitValue,
}: {
  kind: Exclude<FieldControlKind, 'boolean' | 'enum'>;
  fieldType: string | undefined;
  workingValue: unknown;
  ariaLabel: string;
  onCommitValue: (value: unknown) => Promise<CorrectionCommitResult | void>;
}) {
  const serverText = formatFieldDraft(workingValue, kind);
  const [draft, setDraft] = useState<string | null>(null);
  if (draft !== null && draft === serverText) setDraft(null);
  const shown = draft ?? serverText;

  async function handleBlur(
    event: FocusEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) {
    const text = event.currentTarget.value;
    // The control shows a shortened date or datetime. Blurring that text
    // must not write the shortened form back over the stored value.
    if (text === serverText) {
      setDraft(null);
      return;
    }
    const parsed = parseFieldDraft(kind, fieldType, text);
    if (!parsed.ok) return;
    if (!scalarValuesDiffer(parsed.value, workingValue)) {
      setDraft(null);
      return;
    }
    setDraft(formatFieldDraft(parsed.value, kind));
    const result = await onCommitValue(parsed.value);
    if (result === 'conflict' || result === 'failed') setDraft(null);
  }

  if (kind === 'textarea') {
    return (
      <Textarea
        aria-label={ariaLabel}
        value={shown}
        rows={2}
        className="text-sm"
        onChange={(event) => setDraft(event.target.value)}
        onBlur={handleBlur}
      />
    );
  }

  return (
    <Input
      aria-label={ariaLabel}
      type={inputType(kind)}
      step={kind === 'number' ? numberInputStep(fieldType) : undefined}
      value={shown}
      className="text-sm"
      onChange={(event) => setDraft(event.target.value)}
      onBlur={handleBlur}
    />
  );
}

function inputType(
  kind: Exclude<FieldControlKind, 'boolean' | 'enum' | 'textarea'>,
): 'text' | 'number' | 'date' | 'time' | 'datetime-local' {
  if (kind === 'number') return 'number';
  if (kind === 'date') return 'date';
  if (kind === 'time') return 'time';
  if (kind === 'datetime') return 'datetime-local';
  return 'text';
}

function BooleanControl({
  workingValue,
  ariaLabel,
  onCommitValue,
}: {
  workingValue: unknown;
  ariaLabel: string;
  onCommitValue: (value: unknown) => Promise<CorrectionCommitResult | void>;
}) {
  const server = workingValue === true;
  const [pending, setPending] = useState<boolean | null>(null);
  if (pending !== null && pending === server) setPending(null);

  async function handleChange(checked: boolean) {
    if (!scalarValuesDiffer(checked, workingValue)) return;
    setPending(checked);
    const result = await onCommitValue(checked);
    if (result === 'conflict' || result === 'failed') setPending(null);
  }

  return (
    <Checkbox
      aria-label={ariaLabel}
      checked={pending ?? server}
      onCheckedChange={handleChange}
    />
  );
}

function EnumControl({
  workingValue,
  enumValues,
  ariaLabel,
  onCommitValue,
}: {
  workingValue: unknown;
  enumValues: readonly string[];
  ariaLabel: string;
  onCommitValue: (value: unknown) => Promise<CorrectionCommitResult | void>;
}) {
  const gt = useGT();
  const server = typeof workingValue === 'string' ? workingValue : '';
  const [pending, setPending] = useState<string | null>(null);
  if (pending !== null && pending === server) setPending(null);
  const shown = pending ?? server;

  async function handleChange(value: string) {
    const next = value === '' ? null : value;
    if (!scalarValuesDiffer(next, workingValue === '' ? null : workingValue)) {
      return;
    }
    setPending(value);
    const result = await onCommitValue(next);
    if (result === 'conflict' || result === 'failed') setPending(null);
  }

  return (
    <Select value={shown} onValueChange={handleChange}>
      <SelectTrigger aria-label={ariaLabel} className="w-full">
        <SelectValue
          placeholder={gt('Select', {
            context: 'placeholder for an empty task view enum field',
          })}
        />
      </SelectTrigger>
      <SelectContent>
        {enumValues.map((option) => (
          <SelectItem key={option} value={option}>
            {option}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}
